import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API, rupiah, imgSrc, imageRatioClass, imagePositionClass, catLabel, waList } from "@/lib/api";
import WhatsAppPicker from "@/components/WhatsAppPicker";
import VideoPlayer from "@/components/VideoPlayer";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Minus, Plus, ShoppingCart, ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [purchaseUnit, setPurchaseUnit] = useState("roll");
  const [waOpen, setWaOpen] = useState(false);
  const [site, setSite] = useState({});

  useEffect(() => {
    if (user === false) navigate("/masuk?next=/toko/" + id);
  }, [user, navigate, id]);

  useEffect(() => {
    API.get(`/products/${id}`)
      .then((r) => {
        setP(r.data);
        const firstVariant = (r.data.variants || [])[0] || null;
        setVariant(firstVariant);
        setSelectedImage(firstVariant?.image || (r.data.images || [])[0] || "");
        setPurchaseUnit(r.data.category === "tali" && /kg|kilogram/i.test(r.data.unit || "") ? "kg" : (r.data.category === "tali" ? "roll" : (r.data.unit || "pcs")));
      })
      .catch(() => navigate("/toko"));
  }, [id, navigate]);

  useEffect(() => {
    API.get("/settings").then((response) => setSite(response.data || {})).catch(() => {});
  }, []);

  if (!p) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Memuat...</div>;

  const selectedProductImageIndex = (p.images || []).indexOf(selectedImage);
  const selectedVariant = (p.variants || []).find((item) => item.image && item.image === selectedImage);
  const activeImageRatio = selectedProductImageIndex >= 0 ? p.image_ratios?.[selectedProductImageIndex] : selectedVariant?.image_ratio;
  const activeImagePosition = selectedProductImageIndex >= 0 ? p.image_positions?.[selectedProductImageIndex] : selectedVariant?.image_position;
  const baseUnit = /kg|kilogram/i.test(p.unit || "") ? "kg" : (/roll/i.test(p.unit || "") ? "roll" : (p.unit || "pcs"));
  const unitPrice = (item, unit) => Number(p.category !== "tali" || unit === baseUnit ? item?.price : (unit === "kg" ? item?.price_kg : item?.price_roll)) || 0;
  const unitStock = (item, unit) => Number(p.category !== "tali" || unit === baseUnit ? item?.stock : (unit === "kg" ? item?.stock_kg : item?.stock_roll)) || 0;
  const salePrice = unitPrice(variant, purchaseUnit);
  const saleStock = unitStock(variant, purchaseUnit);

  const addToCart = () => {
    if (!variant) return toast.error("Pilih varian terlebih dahulu");
    if (salePrice <= 0 || saleStock <= 0) return toast.error(`Pilihan per ${purchaseUnit} belum tersedia`);
    add(p, { ...variant, price: salePrice }, qty, purchaseUnit);
    toast.success(`${p.name} (${variant.label}) ${qty} ${purchaseUnit} masuk keranjang`);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-10" data-testid="product-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <Link to="/toko" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B1F3A] mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Toko
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className={`${imageRatioClass(activeImageRatio, "1:1")} bg-white border border-slate-200 rounded-md overflow-hidden`} data-testid="product-image">
              <img src={imgSrc(selectedImage)} alt={p.name} className={`w-full h-full object-cover ${imagePositionClass(activeImagePosition)}`} />
            </div>
            {selectedVariant?.image && <p className="text-xs text-slate-400 mt-2" data-testid="variant-image-note">Menampilkan foto varian: {selectedVariant.label}</p>}
            {(p.images || []).length > 1 && (
              <div className="flex gap-2 mt-3">
                {p.images.map((im, i) => (
                  <button key={i} onClick={() => setSelectedImage(im)} data-testid={`thumb-${i}`} aria-label={`Lihat gambar ${i + 1}`} className={`h-16 w-16 rounded-md overflow-hidden border-2 ${selectedImage === im ? "border-[#C9A227]" : "border-slate-200"}`}>
                    <img src={imgSrc(im)} alt="" className={`w-full h-full object-cover ${imagePositionClass(p.image_positions?.[i])}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9A227]">{p.brand || "NAWADAH by NDH Official"} • {catLabel(p.category)}</p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2" data-testid="product-name">{p.name}</h1>
            <p className="text-slate-600 text-sm leading-relaxed mt-4" data-testid="product-desc">{p.description}</p>
            {p.video_url && (
              <div className="mt-6 aspect-video bg-black rounded-md overflow-hidden" data-testid="product-video">
                <VideoPlayer url={p.video_url} title="Video penjelasan produk" className="h-full w-full object-cover" />
              </div>
            )}

            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Pilih Varian</p>
              <div className="flex flex-wrap gap-2" data-testid="variant-list">
                {(p.variants || []).map((v) => (
                  <button
                    key={v.label}
                    onClick={() => { setVariant(v); if (v.image) setSelectedImage(v.image); setQty(1); }}
                    data-testid={`variant-${v.label}`}
                    className={`inline-flex min-w-20 flex-col items-center justify-center px-4 py-2.5 text-center rounded-md border text-sm font-semibold transition-colors ${variant?.label === v.label ? "border-[#0B1F3A] bg-[#0B1F3A] text-white" : "border-slate-300 bg-white hover:border-[#0B1F3A]"}`}
                  >
                    {v.image && <img src={imgSrc(v.image)} alt="" className={`mx-auto mb-1.5 h-9 w-9 rounded object-cover ${imagePositionClass(v.image_position)}`} />}
                    {v.label}
                    <span className="block text-[11px] font-normal opacity-75">Stok {v.stock}</span>
                  </button>
                ))}
              </div>
            </div>

            {p.category === "tali" && variant && (
              <div className="mt-6" data-testid="purchase-unit-selector">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Pilih Satuan Pembelian</p>
                <div className="grid grid-cols-2 gap-3">
                  {[{ value: "roll", label: "Per Roll", price: unitPrice(variant, "roll"), stock: unitStock(variant, "roll") }, { value: "kg", label: "Per Kilogram", price: unitPrice(variant, "kg"), stock: unitStock(variant, "kg") }].map((option) => (
                    <button key={option.value} type="button" onClick={() => { setPurchaseUnit(option.value); setQty(1); }} disabled={Number(option.price || 0) <= 0} data-testid={`purchase-unit-${option.value}`} className={`rounded-md border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${purchaseUnit === option.value ? "border-[#C9A227] bg-amber-50" : "border-slate-300 bg-white"}`}>
                      <span className="block text-sm font-bold text-[#0B1F3A]">{option.label}</span>
                      <span className="mt-1 block text-xs text-slate-500">{Number(option.price || 0) > 0 ? `${rupiah(option.price)} • Stok ${Number(option.stock || 0)}` : "Belum tersedia"}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {variant && (
              <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Harga per {purchaseUnit || p.unit || "pcs"}</span>
                  <span className="font-display text-2xl font-bold text-[#0B1F3A]" data-testid="product-price">{salePrice > 0 ? rupiah(salePrice) : "Belum tersedia"}</span>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-slate-500">Jumlah</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="h-9 w-9 border border-slate-300 rounded-md flex items-center justify-center hover:border-[#0B1F3A] transition-colors">
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={Math.max(1, saleStock)}
                      value={qty}
                      onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                      data-testid="qty-input"
                      className="font-bold w-14 text-center border border-slate-300 rounded-md py-1 focus:outline-none focus:ring-2 focus:ring-[#C9A227] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="h-9 w-9 border border-slate-300 rounded-md flex items-center justify-center hover:border-[#0B1F3A] transition-colors">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={addToCart}
                disabled={!variant || saleStock <= 0 || salePrice <= 0}
                data-testid="add-to-cart-button"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#0B1F3A] bg-navy-hover text-white font-bold text-sm rounded-md transition-colors disabled:opacity-50"
              >
                <ShoppingCart className="h-4 w-4" /> Tambah ke Keranjang
              </button>
              <button
                onClick={() => setWaOpen(true)}
                data-testid="ask-wa-button"
                title="Tanya via WhatsApp"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-[#0B1F3A] text-[#0B1F3A] font-bold text-sm rounded-md hover:bg-[#0B1F3A] hover:text-white transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-4">Ongkos kirim akan dihitung oleh pemilik setelah pesanan dibuat dan diinfokan sebelum pembayaran.</p>
          </div>
        </div>
      </div>

      <WhatsAppPicker
        open={waOpen}
        onClose={() => setWaOpen(false)}
        contacts={waList(site)}
        message={`Halo Admin NAWADAH, saya ingin bertanya tentang produk: ${p.name}`}
      />
    </div>
  );
}
